import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Users, Briefcase, HeartHandshake, Building2, Lock, Calendar, CalendarDays, CalendarRange } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = [
  { key: "professional", label: "Profesionales", color: "#1F4E47", Icon: Users },
  { key: "entrepreneur", label: "Emprendimientos", color: "#E97A3A", Icon: Briefcase },
  { key: "community", label: "Comunidad", color: "#8B5CF6", Icon: HeartHandshake },
  { key: "entity", label: "Entidades", color: "#F59E0B", Icon: Building2 },
];

const GRAN_OPTS = [
  { key: "daily", label: "Diario · Últimos 30 días" },
  { key: "weekly", label: "Semanal · Últimas 12 semanas" },
  { key: "monthly", label: "Mensual · Últimos 12 meses" },
];

const StatBadge = ({ Icon, color, label, value, sub }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-start gap-3">
    <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20`, color }}>
      <Icon size={20} />
    </span>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wider font-bold text-teal-soft">{label}</div>
      <div className="text-3xl font-display text-teal-deep mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-teal-soft mt-0.5">{sub}</div>}
    </div>
  </div>
);

export default function PublicStats() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [gran, setGran] = useState("daily");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setError("Falta el token de acceso en el enlace."); return; }
      try {
        const { data } = await axios.get(`${API}/public/signups-stats`, { params: { token } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 403 ? "Enlace no válido. Solicita uno nuevo al administrador." : "No se pudieron cargar las estadísticas.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const rows = useMemo(() => (data ? [...(data[gran] || [])].reverse() : []), [data, gran]);

  const sumRow = useCallback((row) => KIND_META.reduce((s, k) => s + (row?.[k.key] || 0), 0), []);
  const periodSummary = useMemo(() => {
    if (!data) return null;
    const dailyRows = data.daily || [];
    const weeklyRows = data.weekly || [];
    const monthlyRows = data.monthly || [];
    const today = dailyRows[dailyRows.length - 1];
    const week = weeklyRows[weeklyRows.length - 1];
    const month = monthlyRows[monthlyRows.length - 1];
    return {
      today: { total: sumRow(today), row: today, prev: dailyRows[dailyRows.length - 2] },
      week: { total: sumRow(week), row: week, prev: weeklyRows[weeklyRows.length - 2] },
      month: { total: sumRow(month), row: month, prev: monthlyRows[monthlyRows.length - 2] },
    };
  }, [data, sumRow]);
  const tickFmt = (d) => {
    if (gran === "daily") return (d || "").slice(5);
    if (gran === "weekly") return (d || "").replace(/^\d{4}-/, "");
    return d;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="max-w-md text-center bg-white rounded-2xl border border-gray-200 p-8" data-testid="public-stats-error">
          <div className="w-14 h-14 rounded-full bg-orange/10 text-orange mx-auto mb-4 flex items-center justify-center">
            <Lock size={22} />
          </div>
          <h1 className="font-display text-2xl text-teal-deep mb-2">Acceso restringido</h1>
          <p className="text-teal-soft text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="public-stats-page">
        {/* Header */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-orange mb-2">RED de Amor y Solidaridad</p>
          <h1 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight">Registros por tipo de perfil</h1>
          <p className="text-teal-soft mt-2 max-w-2xl">Reporte en vivo con inscritos diarios, semanales y mensuales, categorizados por perfil. Los datos se actualizan al recargar la página.</p>
        </div>

        {!data ? (
          <div className="text-teal-soft text-sm">Cargando estadísticas…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="public-stats-totals">
              {KIND_META.map((k) => (
                <StatBadge key={k.key} Icon={k.Icon} color={k.color} label={k.label} value={data.totals?.[k.key] || 0} sub="Total histórico" />
              ))}
            </div>

            {periodSummary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8" data-testid="public-stats-period-summary">
                <StatBadge
                  Icon={Calendar}
                  color="#E97A3A"
                  label="Hoy"
                  value={periodSummary.today.total}
                  sub={periodSummary.today.row?.date || ""}
                />
                <StatBadge
                  Icon={CalendarDays}
                  color="#1F4E47"
                  label="Esta semana"
                  value={periodSummary.week.total}
                  sub={periodSummary.week.row?.date || ""}
                />
                <StatBadge
                  Icon={CalendarRange}
                  color="#8B5CF6"
                  label="Este mes"
                  value={periodSummary.month.total}
                  sub={periodSummary.month.row?.date || ""}
                />
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              {GRAN_OPTS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setGran(opt.key)}
                  data-testid={`public-signup-gran-${opt.key}`}
                  className={`text-xs font-bold uppercase tracking-wider rounded-full px-4 py-2 transition-colors ${
                    gran === opt.key ? "bg-teal-deep text-white" : "bg-white text-teal border border-teal/20 hover:bg-teal/10"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6" data-testid={`public-signup-chart-${gran}`}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data[gran]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={tickFmt} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  {KIND_META.map((k) => (
                    <Bar key={k.key} dataKey={k.key} stackId="a" fill={k.color} name={k.label} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`public-signup-table-${gran}`}>
                  <thead className="bg-cream border-b border-gray-200">
                    <tr className="text-left text-teal">
                      <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Periodo</th>
                      {KIND_META.map((k) => (
                        <th key={k.key} className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">{k.label}</th>
                      ))}
                      <th className="px-4 py-3 font-bold uppercase text-xs tracking-wider text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const total = KIND_META.reduce((sum, k) => sum + (row[k.key] || 0), 0);
                      return (
                        <tr key={row.date} className="border-b border-gray-100 hover:bg-cream/40">
                          <td className="px-4 py-2 font-semibold text-teal-deep">{row.date}</td>
                          {KIND_META.map((k) => (
                            <td key={k.key} className="px-4 py-2 text-teal-soft text-right">{row[k.key] || 0}</td>
                          ))}
                          <td className="px-4 py-2 font-bold text-teal-deep text-right">{total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-teal-soft mt-6 text-center">Este enlace es privado. No lo compartas con terceros.</p>
          </>
        )}
      </div>
    </div>
  );
}
